import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { useEffect } from "react";
import type { Database } from "@/integrations/supabase/types";

type ConnectionStatus = Database["public"]["Enums"]["connection_status_v2"];

export interface ConnectionProfile {
  id: string;
  full_name: string;
  role: string;
  avatar_url: string | null;
  bio: string | null;
}

export interface CommunityConnection {
  id: string;
  requester_id: string;
  recipient_id: string;
  status: ConnectionStatus;
  created_at: string;
  updated_at: string;
  accepted_at: string | null;
  connection_type: "community" | "mentorship";
  message: string | null;
  scheduled_at: string | null;
  zoom_link: string | null;
  thread_id: string | null;
  reminder_24h_sent: boolean;
  reminder_48h_sent: boolean;
  reminder_72h_sent: boolean;
  admin_approved_by: string | null;
  admin_approved_at: string | null;
  is_mentorship_active: boolean;
  mentorship_completion_note: string | null;
  mentorship_completed_at: string | null;
  requester?: ConnectionProfile;
  recipient?: ConnectionProfile;
}

/**
 * Fetches the connection between the current user and a target user
 */
export function useConnectionWithUser(targetUserId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["community_connection", user?.id, targetUserId],
    queryFn: async () => {
      if (!user?.id || !targetUserId || user.id === targetUserId) return null;

      const { data, error } = await supabase
        .from("connections")
        .select("*")
        .eq("connection_type", "community")
        .or(
          `and(requester_id.eq.${user.id},recipient_id.eq.${targetUserId}),and(requester_id.eq.${targetUserId},recipient_id.eq.${user.id})`
        )
        .maybeSingle();

      if (error) throw error;
      return data as CommunityConnection | null;
    },
    enabled: !!user?.id && !!targetUserId && user.id !== targetUserId,
  });
}

/**
 * Fetch all accepted connections with profile data
 */
export function useMyConnections() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["my_connections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("connections")
        .select(`
          *,
          requester:profiles!connections_requester_id_fkey(id, full_name, role, avatar_url, bio),
          recipient:profiles!connections_recipient_id_fkey(id, full_name, role, avatar_url, bio)
        `)
        .eq("connection_type", "community")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .eq("status", "accepted")
        .order("accepted_at", { ascending: false });

      if (error) throw error;
      return data as CommunityConnection[];
    },
    enabled: !!user?.id,
  });
}

/**
 * Fetch sent pending requests (I'm the requester)
 */
export function useSentConnectionRequests() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["sent_connection_requests", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("connections")
        .select(`
          *,
          recipient:profiles!connections_recipient_id_fkey(id, full_name, role, avatar_url, bio)
        `)
        .eq("connection_type", "community")
        .eq("requester_id", user.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CommunityConnection[];
    },
    enabled: !!user?.id,
  });
}

/**
 * Fetch received pending requests (I'm the recipient)
 */
export function useReceivedConnectionRequests() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["received_connection_requests", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("connections")
        .select(`
          *,
          requester:profiles!connections_requester_id_fkey(id, full_name, role, avatar_url, bio)
        `)
        .eq("connection_type", "community")
        .eq("recipient_id", user.id)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CommunityConnection[];
    },
    enabled: !!user?.id,
  });
}

/**
 * Send a connection request
 */
export function useSendConnectionRequest() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (recipientId: string) => {
      if (!user?.id) throw new Error("Not authenticated");

      // Use the create_connection_request RPC rather than a raw insert.
      // The raw insert collided with unique_connection_per_type
      // (requester_id, recipient_id, connection_type) whenever a previously
      // DECLINED row existed, so "Connect Again" after a decline always failed.
      // The RPC deletes the declined row first, then inserts.
      const { data, error } = await supabase.rpc("create_connection_request", {
        p_recipient_id: recipientId,
        p_message: null,
      });

      if (error) throw error;

      // The RPC swallows its own errors: `EXCEPTION WHEN OTHERS THEN RETURN
      // json_build_object('success', false, ...)`. That returns HTTP 200, so
      // `error` is null on failure and the mutation would report success.
      const result = data as { success?: boolean; error?: string } | null;
      if (!result?.success) {
        throw new Error(result?.error || "Failed to send connection request");
      }
      return result;
    },
    onSuccess: (_, recipientId) => {
      queryClient.invalidateQueries({ queryKey: ["community_connection", user?.id, recipientId] });
      queryClient.invalidateQueries({ queryKey: ["sent_connection_requests"] });
      queryClient.invalidateQueries({ queryKey: ["my_connections"] });
      toast.success("Connection request sent!");
    },
    onError: (error: Error) => {
      if (error.message.includes("duplicate")) {
        toast.error("A connection request already exists");
      } else if (error.message.includes("self-connection")) {
        toast.error("You cannot connect with yourself");
      } else {
        toast.error("Failed to send request: " + error.message);
      }
    },
  });
}

/**
 * Accept a connection request
 */
export function useAcceptConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ connectionId, requesterName }: { connectionId: string; requesterName?: string }) => {
      const { data, error } = await supabase
        .from("connections")
        .update({ status: "accepted" })
        .eq("id", connectionId)
        .select()
        .single();

      if (error) throw error;
      return { data, requesterName };
    },
    onSuccess: ({ requesterName }) => {
      queryClient.invalidateQueries({ queryKey: ["community_connection"] });
      queryClient.invalidateQueries({ queryKey: ["my_connections"] });
      queryClient.invalidateQueries({ queryKey: ["received_connection_requests"] });
      toast.success(requesterName ? `Connected with ${requesterName}!` : "Connection accepted!");
    },
    onError: (error: Error) => {
      toast.error("Failed to accept: " + error.message);
    },
  });
}

/**
 * Decline a connection request
 */
export function useDeclineConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      const { data, error } = await supabase
        .from("connections")
        .update({ status: "declined" })
        .eq("id", connectionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["community_connection"] });
      queryClient.invalidateQueries({ queryKey: ["received_connection_requests"] });
      toast.success("Request declined");
    },
    onError: (error: Error) => {
      toast.error("Failed to decline: " + error.message);
    },
  });
}

/**
 * Cancel a sent connection request
 */
export function useCancelConnectionRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      const { error } = await supabase
        .from("connections")
        .delete()
        .eq("id", connectionId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["community_connection"] });
      queryClient.invalidateQueries({ queryKey: ["sent_connection_requests"] });
      toast.success("Request cancelled");
    },
    onError: (error: Error) => {
      toast.error("Failed to cancel: " + error.message);
    },
  });
}

/**
 * Remove/delete a connection
 */
export function useRemoveConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ connectionId, personName }: { connectionId: string; personName?: string }) => {
      const { error } = await supabase
        .from("connections")
        .delete()
        .eq("id", connectionId);

      if (error) throw error;
      return { personName };
    },
    onSuccess: ({ personName }) => {
      queryClient.invalidateQueries({ queryKey: ["community_connection"] });
      queryClient.invalidateQueries({ queryKey: ["my_connections"] });
      toast.success(personName ? `Connection with ${personName} removed` : "Connection removed");
    },
    onError: (error: Error) => {
      toast.error("Failed to remove connection: " + error.message);
    },
  });
}

/**
 * Setup real-time subscription for connections
 */
export function useConnectionsRealtime() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("connections-realtime")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "connections",
          filter: `requester_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["my_connections"] });
          queryClient.invalidateQueries({ queryKey: ["sent_connection_requests"] });
          queryClient.invalidateQueries({ queryKey: ["received_connection_requests"] });
          queryClient.invalidateQueries({ queryKey: ["community_connection"] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "connections",
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["my_connections"] });
          queryClient.invalidateQueries({ queryKey: ["sent_connection_requests"] });
          queryClient.invalidateQueries({ queryKey: ["received_connection_requests"] });
          queryClient.invalidateQueries({ queryKey: ["community_connection"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);
}

/**
 * Fetch all connections for the current user (all statuses)
 */
export function useCommunityConnections() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["community_connections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("connections")
        .select("*")
        .or(`requester_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as CommunityConnection[];
    },
    enabled: !!user?.id,
  });
}
